import { Component, Input, Output, EventEmitter, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TemplateService } from '../../../core/services/template.service';
import { AlertService } from '../../../core/services/alert.service';
import { Template, TemplateType, ListTemplatesQueryDto } from '../../../core/models/template.interface';
import { AnyBlock } from '../../../core/models/article-block.interface';
import { ArticleBuilderStructure } from '../../../core/models/blog.interface';

@Component({
  selector: 'app-template-load-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './template-load-modal.component.html',
  styleUrls: ['./template-load-modal.component.scss']
})
export class TemplateLoadModalComponent implements OnInit {
  @Input() visible = false;
  @Output() loaded = new EventEmitter<AnyBlock[]>();
  @Output() closed = new EventEmitter<void>();

  private templateService = inject(TemplateService);
  private alertService = inject(AlertService);

  templates = signal<Template[]>([]);
  loading = signal(false);
  actionLoading = signal(false);
  selectedTemplate = signal<Template | null>(null);
  
  searchTerm = '';
  selectedType = '';
  showConfirmModal = false;

  ngOnInit(): void {
    if (this.visible) {
      this.loadTemplates();
    }
  }

  ngOnChanges(): void {
    if (this.visible) {
      this.loadTemplates();
    }
  }

  loadTemplates(): void {
    this.loading.set(true);
    
    const query: ListTemplatesQueryDto = {};
    if (this.searchTerm) {
      query.search = this.searchTerm;
    }
    if (this.selectedType) {
      query.type = this.selectedType as TemplateType;
    }

    this.templateService.getTemplates(query).subscribe({
      next: (templates) => {
        this.templates.set(templates);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Errore nel caricamento dei template:', error);
        this.alertService.error('Errore nel caricamento dei template');
        this.loading.set(false);
      }
    });
  }

  onSearchChange(): void {
    // Debounce search
    setTimeout(() => {
      this.loadTemplates();
    }, 300);
  }

  onTypeChange(): void {
    this.loadTemplates();
  }

  selectTemplate(template: Template): void {
    this.selectedTemplate.set(template);
  }

  onLoad(): void {
    if (!this.selectedTemplate()) {
      return;
    }
    this.showConfirmModal = true;
  }

  confirmLoad(): void {
    if (!this.selectedTemplate()) {
      return;
    }

    this.showConfirmModal = false;
    this.actionLoading.set(true);

    const template = this.selectedTemplate()!;
    const blocks = this.mapBuilderStructureToBlocks(template.builderData);
    
    this.loaded.emit(blocks);
    this.actionLoading.set(false);
    this.onCancel();
  }

  cancelLoad(): void {
    this.showConfirmModal = false;
  }


  onDuplicate(template: Template, event: Event): void {
    event.stopPropagation();
    
    const newName = prompt('Nome per il template duplicato:', `${template.name} - Copia`);
    if (!newName) {
      return;
    }

    this.templateService.duplicateTemplate(template.id, { name: newName }).subscribe({
      next: () => {
        this.alertService.success('Template duplicato con successo!');
        this.loadTemplates();
      },
      error: (error) => {
        console.error('Errore nella duplicazione del template:', error);
        this.alertService.error(error.error?.message || 'Errore nella duplicazione del template');
      }
    });
  }

  onDelete(template: Template, event: Event): void {
    event.stopPropagation();
    
    if (!confirm(`Sei sicuro di voler eliminare il template "${template.name}"?`)) {
      return;
    }

    this.templateService.deleteTemplate(template.id).subscribe({
      next: () => {
        this.alertService.success('Template eliminato con successo!');
        this.loadTemplates();
        if (this.selectedTemplate()?.id === template.id) {
          this.selectedTemplate.set(null);
        }
      },
      error: (error) => {
        console.error('Errore nell\'eliminazione del template:', error);
        this.alertService.error('Errore nell\'eliminazione del template');
      }
    });
  }

  onCancel(): void {
    this.selectedTemplate.set(null);
    this.searchTerm = '';
    this.selectedType = '';
    this.showConfirmModal = false;
    this.closed.emit();
  }

  getTypeLabel(type: TemplateType): string {
    switch (type) {
      case TemplateType.STRUCTURE_ONLY:
        return 'Solo Struttura';
      case TemplateType.WITH_CONTENT:
        return 'Con Contenuto';
      default:
        return type;
    }
  }

  getTypeBadgeClass(type: TemplateType): string {
    switch (type) {
      case TemplateType.STRUCTURE_ONLY:
        return 'badge bg-primary';
      case TemplateType.WITH_CONTENT:
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private mapBuilderStructureToBlocks(structure: ArticleBuilderStructure): AnyBlock[] {
    if (!structure?.sections?.length) {
      return [];
    }

    const components = structure.sections.flatMap((section) => section.components ?? []);
    const blocks = components
      .map((component, index) => this.mapComponentToBlock(component, index))
      .filter((block): block is AnyBlock => !!block)
      .map((block, index) => ({ ...block, order: index }));

    return blocks;
  }

  private mapComponentToBlock(component: any, fallbackOrder: number): AnyBlock | null {
    const order = component.meta?.['order'] ?? fallbackOrder;
    const id = component.id ?? this.generateBlockId(component.type);
    const content = component.content ?? {};

    switch (component.type) {
      case 'text':
        return {
          id,
          type: 'text',
          order,
          content: content['html'] ?? '',
        };
      case 'heading':
        return {
          id,
          type: 'heading',
          order,
          level: this.clampHeadingLevel(content['level']),
          text: content['text'] ?? '',
        };
      case 'image':
        return {
          id,
          type: 'image',
          order,
          url: content['url'] ?? '',
          alt: content['alt'] ?? '',
          caption: content['caption'] ?? '',
          width: content['width'] ?? 'full',
        };
      case 'quote':
        return {
          id,
          type: 'quote',
          order,
          text: content['text'] ?? '',
          author: content['author'] ?? '',
        };
      case 'spacer':
        return {
          id,
          type: 'spacer',
          order,
          height: content['height'] ?? 40,
        };
      case 'divider':
        return {
          id,
          type: 'divider',
          order,
          marginY: content['marginY'] ?? 'my-5',
        };
      default:
        return null;
    }
  }

  private generateBlockId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private clampHeadingLevel(value: any): 1 | 2 | 3 | 4 | 5 | 6 {
    const numeric = Number(value ?? 2);
    const normalized = Number.isFinite(numeric) ? Math.round(numeric) : 2;
    const clamped = Math.min(Math.max(normalized, 1), 6);
    return clamped as 1 | 2 | 3 | 4 | 5 | 6;
  }
}
